import { z } from 'zod';

// ── Kontrak kustomisasi web self-service admin tenant ───────────────────────
// Dipakai apps/api (validasi PUT) dan apps/web (form + renderer).
// Penyimpanan: landing/nav/pages di tenant DB app_settings; branding di
// control plane (subset editable via tenantBrandingUpdateSchema).

// href yang aman untuk link buatan tenant: path relatif atau http(s).
const safeHref = z
  .string()
  .max(300)
  .refine((v) => /^\/(?!\/)/.test(v) || /^https?:\/\//i.test(v), {
    message: 'href harus path relatif atau URL http(s)',
  });

// ── Branding: subset yang boleh diubah admin tenant (TANPA email.*) ─────────
export const tenantThemeSchema = z
  .object({
    radius: z.enum(['sm', 'md', 'lg', 'xl']).optional(),
    font: z.enum(['system', 'inter', 'georgia', 'mono']).optional(),
    /** Font kustom: URL file font (woff2/ttf, diupload ke R2) + nama family. Jika keduanya
     *  ada, menang atas preset `font`. Di-inject sebagai @font-face oleh buildBrandCssVars. */
    font_url: z.string().url().optional(),
    font_family: z.string().max(60).optional(),
    /** Preset warna latar terkurasi (bukan hex bebas — jaga kontras teks). */
    background: z.enum(['default', 'warm', 'cool', 'white']).optional(),
    /** Gambar latar bebas untuk halaman login/register (URL R2). Kosong = pakai preset panel. */
    auth_background_url: z.string().url().optional(),
    /** Seberapa jauh warna brand merembes ke UI; absen = 'subtle' (tanpa token ekstra). */
    intensity: z.enum(['subtle', 'balanced', 'bold']).optional(),
    /** Elevasi/border kartu section (landing) — absen = 'flat' (tampilan sekarang). */
    card_style: z.enum(['flat', 'shadow', 'bordered', 'glass']).optional(),
    /** Layer dekoratif latar (gradient/pola) — TERPISAH dari `background` (warna
     *  flat, dipetakan ke --surface). Sengaja tidak menimpa --surface: --surface
     *  juga dipakai sebagai background-color murni di halaman auth
     *  (bg-[var(--surface)]), yang tak bisa menampung nilai gradient. Absen/'flat'
     *  = tanpa layer tambahan (pixel-identik dengan sebelum field ini ada). */
    background_style: z.enum(['flat', 'gradient_soft', 'gradient_radial', 'pattern_dots', 'pattern_grid']).optional(),
  })
  .strict();

export type TenantTheme = z.infer<typeof tenantThemeSchema>;

export const tenantBrandingUpdateSchema = z
  .object({
    brand_name: z.string().min(1).max(60).optional(),
    logo_url: z.string().url().max(500).optional(),
    favicon_url: z.string().url().max(500).optional(),
    colors: z
      .object({
        primary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        accent: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
      })
      .optional(),
    landing: z
      .object({
        tagline: z.string().max(200).optional(),
        og_image_url: z.string().url().max(500).optional(),
      })
      .optional(),
    analytics: z
      .object({
        ga_id: z.string().regex(/^[A-Za-z0-9_-]*$/).max(30).optional(),
        // Pixel iklan (injeksi <head> oleh web) — pola id sama dengan ga_id;
        // '*' (bukan '+') agar string kosong bisa dipakai untuk clear.
        meta_pixel_id: z.string().regex(/^[A-Za-z0-9_-]*$/).max(40).optional(),
        // Meta Conversions API access token — untuk server-side tracking Purchase event.
        // Max 200 char (token biasa ~180 char). Kosong = CAPI disabled.
        meta_capi_token: z.string().max(200).optional(),
        tiktok_pixel_id: z.string().regex(/^[A-Za-z0-9_-]*$/).max(40).optional(),
      })
      .optional(),
    support_email: z.string().email().optional(),
    theme: tenantThemeSchema.optional(),
    seo: z
      .object({
        /** Pengganti nama brand di <title> ter-rewrite edge — mis. "Bimbel X — Persiapan UTBK #1". */
        meta_title_suffix: z.string().max(70).optional(),
        /** Menang atas landing.tagline sebagai meta description. */
        meta_description: z.string().max(160).optional(),
      })
      .optional(),
  })
  .strict();

export type TenantBrandingUpdate = z.infer<typeof tenantBrandingUpdateSchema>;

// ── Landing config (app_settings key: 'landing_config') ────────────────────
const sectionBase = {
  id: z.string().min(1).max(40),
  hidden: z.boolean().default(false),
};

export const landingSectionSchema = z.discriminatedUnion('type', [
  z.object({
    ...sectionBase,
    type: z.literal('hero'),
    heading: z.string().max(120),
    subheading: z.string().max(300).optional(),
    image_url: z.string().url().max(500).optional(),
    cta: z.object({ label: z.string().max(40), href: safeHref }).optional(),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('about'),
    heading: z.string().max(120).optional(),
    body_html: z.string().max(10_000),
  }),
  // HTML penuh + JS buatan admin (mis. hasil generate AI). Disimpan RAW (TIDAK
  // di-sanitasi) — keamanan dijamin oleh RENDERER: di-render dalam iframe
  // sandbox="allow-scripts …" TANPA allow-same-origin (CustomHtmlSection),
  // sehingga JS berjalan di origin opaque dan tak bisa membaca cookie/sesi/DOM
  // tenant. JANGAN pernah menyuntik html ini langsung ke DOM utama.
  z.object({
    ...sectionBase,
    type: z.literal('custom_html'),
    html: z.string().max(100_000),
    /** 'auto' = tinggi mengikuti konten (via postMessage); 'fixed' = height_px. */
    height_mode: z.enum(['auto', 'fixed']).default('auto'),
    height_px: z.number().int().min(80).max(4000).optional(),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('featured_courses'),
    heading: z.string().max(120).default('Kelas Unggulan'),
    limit: z.number().int().min(1).max(12).default(6),
    category: z.string().max(60).optional(),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('featured_exams'),
    heading: z.string().max(120).default('Tryout'),
    limit: z.number().int().min(1).max(12).default(6),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('pricing'),
    heading: z.string().max(120).default('Paket Belajar'),
  }),
  // Hitung mundur ke tanggal target (mis. penutupan pendaftaran batch). Renderer
  // menghitung sisa waktu di client dari target_at (ISO 8601 UTC).
  z.object({
    ...sectionBase,
    type: z.literal('countdown'),
    heading: z.string().max(120).default('Penawaran Berakhir Dalam'),
    subheading: z.string().max(300).optional(),
    target_at: z.string().datetime({ offset: true }),
    expired_message: z.string().max(200).default('Penawaran telah berakhir.'),
    cta: z.object({ label: z.string().max(40), href: safeHref }).optional(),
  }),
  // Tabel harga statis/interaktif buatan admin (BEDA dari 'pricing' yang menarik
  // plan membership live) — kolom tier bebas, tandai satu sebagai unggulan.
  z.object({
    ...sectionBase,
    type: z.literal('pricing_table'),
    heading: z.string().max(120).default('Pilih Paket'),
    subheading: z.string().max(300).optional(),
    tiers: z
      .array(
        z.object({
          name: z.string().min(1).max(60),
          price_label: z.string().min(1).max(40),
          period_label: z.string().max(30).optional(),
          features: z.array(z.string().max(80)).max(10).default([]),
          cta: z.object({ label: z.string().max(40), href: safeHref }),
          highlighted: z.boolean().default(false),
        })
      )
      .min(1)
      .max(4),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('testimonials'),
    heading: z.string().max(120).optional(),
    items: z
      .array(
        z.object({
          name: z.string().max(80),
          role: z.string().max(80).optional(),
          quote: z.string().max(500),
          photo_url: z.string().url().max(500).optional(),
        })
      )
      .max(12),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('faq'),
    heading: z.string().max(120).optional(),
    items: z
      .array(z.object({ q: z.string().max(200), a_html: z.string().max(4000) }))
      .max(20),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('cta_whatsapp'),
    heading: z.string().max(120),
    wa_number: z.string().regex(/^\+?\d{8,15}$/),
    message: z.string().max(300).optional(),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('gallery'),
    heading: z.string().max(120).optional(),
    images: z
      .array(
        z.object({
          url: z.string().url().max(500),
          caption: z.string().max(120).optional(),
        })
      )
      .min(1)
      .max(12),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('video'),
    heading: z.string().max(120).optional(),
    provider: z.enum(['youtube', 'vimeo']),
    /** ID video mentah (bukan URL) — di-embed via URL resmi provider. */
    video_id: z.string().regex(/^[A-Za-z0-9_-]{5,20}$/),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('stats'),
    heading: z.string().max(120).optional(),
    items: z
      .array(z.object({ value: z.string().max(20), label: z.string().max(60) }))
      .min(1)
      .max(6),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('teachers'),
    heading: z.string().max(120).optional(),
    items: z
      .array(
        z.object({
          name: z.string().max(80),
          role: z.string().max(80).optional(),
          photo_url: z.string().url().max(500).optional(),
          bio: z.string().max(200).optional(),
        })
      )
      .min(1)
      .max(8),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('logos'),
    heading: z.string().max(120).optional(),
    items: z
      .array(
        z.object({
          image_url: z.string().url().max(500),
          alt: z.string().max(60).optional(),
          href: safeHref.optional(),
        })
      )
      .min(1)
      .max(12),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('benefits'),
    heading: z.string().max(120).optional(),
    items: z
      .array(z.object({ title: z.string().max(60), description: z.string().max(200) }))
      .min(1)
      .max(8),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('links'),
    title: z.string().max(80).optional(),
    items: z
      .array(
        z.object({
          label: z.string().min(1).max(60),
          url: z.string().url().max(300),
          icon: z.string().max(30).optional(),
        })
      )
      .max(15),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('socials'),
    title: z.string().max(80).optional(),
    instagram: z.string().max(200).optional(),
    tiktok: z.string().max(200).optional(),
    youtube: z.string().max(200).optional(),
    x: z.string().max(200).optional(),
    facebook: z.string().max(200).optional(),
    website: z.string().max(200).optional(),
  }),
  z.object({
    ...sectionBase,
    type: z.literal('footer'),
    description: z.string().max(300).optional(),
    links: z.array(z.object({ label: z.string().max(40), href: safeHref })).max(12),
    socials: z
      .object({
        instagram: z.string().url().max(300).optional(),
        tiktok: z.string().url().max(300).optional(),
        youtube: z.string().url().max(300).optional(),
      })
      .optional(),
  }),
]);

export type LandingSection = z.infer<typeof landingSectionSchema>;

export const landingConfigSchema = z.object({
  enabled: z.boolean().default(false),
  sections: z.array(landingSectionSchema).max(20).default([]),
});

export type LandingConfig = z.infer<typeof landingConfigSchema>;

export const DEFAULT_LANDING_CONFIG: LandingConfig = { enabled: false, sections: [] };

// ── Store / etalase (app_settings key: 'store_config') ─────────────────────
// Halaman publik /store ala Linktree/lynk.id: campuran blok "section produk"
// (kurasi manual kelas/produk digital/bundle) & widget (link, sosmed, gambar,
// teks, embed). Admin menyusun & mengurutkan blok. Referensi item via SLUG
// (stabil) — di-resolve server saat render.
const storeBlockBase = {
  id: z.string().min(1).max(40),
  hidden: z.boolean().default(false),
};

const storeItemRefSchema = z.object({
  // 'membership' ditambahkan untuk block product_offer (halaman promosi) —
  // aditif murni, product_section (etalase) tetap jalan seperti semula untuk
  // 3 kind lama; picker etalase belum menawarkannya, tak ada perubahan perilaku.
  kind: z.enum(['course', 'digital_product', 'bundle', 'membership']),
  slug: z.string().min(1).max(160),
});

export const storeBlockSchema = z.discriminatedUnion('type', [
  z.object({
    ...storeBlockBase,
    type: z.literal('product_section'),
    heading: z.string().max(120),
    subheading: z.string().max(300).optional(),
    layout: z.enum(['grid', 'carousel']).default('grid'),
    items: z.array(storeItemRefSchema).max(24).default([]),
  }),
  z.object({
    ...storeBlockBase,
    type: z.literal('link'),
    label: z.string().min(1).max(80),
    url: z.string().url().max(500),
    icon: z.string().max(30).optional(),
    description: z.string().max(160).optional(),
  }),
  z.object({
    ...storeBlockBase,
    type: z.literal('socials'),
    title: z.string().max(80).optional(),
    instagram: z.string().max(200).optional(),
    tiktok: z.string().max(200).optional(),
    youtube: z.string().max(200).optional(),
    x: z.string().max(200).optional(),
    facebook: z.string().max(200).optional(),
    website: z.string().max(200).optional(),
  }),
  z.object({
    ...storeBlockBase,
    type: z.literal('image'),
    image_url: z.string().url().max(500),
    href: safeHref.optional(),
    caption: z.string().max(160).optional(),
  }),
  z.object({
    ...storeBlockBase,
    type: z.literal('text'),
    body_html: z.string().max(10_000),
  }),
  // Embed HTML+JS penuh — sama seperti landing 'custom_html': disimpan RAW,
  // dirender di iframe sandbox tanpa allow-same-origin (aman dari pencurian sesi).
  z.object({
    ...storeBlockBase,
    type: z.literal('embed'),
    html: z.string().max(100_000),
    height_mode: z.enum(['auto', 'fixed']).default('auto'),
    height_px: z.number().int().min(80).max(4000).optional(),
  }),
]);

export type StoreBlock = z.infer<typeof storeBlockSchema>;

export const storeConfigSchema = z.object({
  enabled: z.boolean().default(false),
  /** true = /store menggantikan '/' sebagai beranda; landing (bila ada) tak di-root. */
  as_home: z.boolean().default(false),
  title: z.string().max(120).optional(),
  avatar_url: z.string().url().max(500).optional(),
  bio: z.string().max(500).optional(),
  /** Banner full-width di atas header (opsional). */
  cover_url: z.string().url().max(500).optional(),
  /** Badge centang biru di samping judul. */
  verified: z.boolean().default(false),
  /** Gaya visual tombol link: kaca (default) / solid / outline. */
  button_style: z.enum(['glass', 'solid', 'outline']).default('glass'),
  /** Tata letak halaman toko — independen dari button_style/accent_color.
   *  mosaic_grid = grid bento media-padat (ala Beacons); banner_hero = cover
   *  full-bleed dominan dengan avatar menumpuk (ala Lynk.id). */
  layout_variant: z.enum(['classic', 'minimal_list', 'grid_card', 'bio_center', 'mosaic_grid', 'banner_hero']).default('classic'),
  /** Override warna glow/tema store (hex). Kosong = pakai aksen brand. */
  accent_color: z.string().regex(/^#([0-9a-fA-F]{6})$/).optional(),
  /** Gaya layer latar — dibangun dari --st-accent (bukan hex kurasi terpisah),
   *  jadi otomatis selaras warna aksen apa pun. Independen dari accent_color
   *  (yang tetap mewarnai tombol/ikon). */
  background_style: z.enum(['flat', 'gradient', 'pattern_dots', 'pattern_grid']).default('flat'),
  blocks: z.array(storeBlockSchema).max(24).default([]),
});

export type StoreConfig = z.infer<typeof storeConfigSchema>;

export const DEFAULT_STORE_CONFIG: StoreConfig = {
  enabled: false,
  as_home: false,
  verified: false,
  button_style: 'glass',
  layout_variant: 'classic',
  background_style: 'flat',
  blocks: [],
};

// ── Halaman promosi / landing page builder per-produk (tabel `landing_pages`) ──
// Beda dari landingSectionSchema di atas (beranda TUNGGAL tenant): tenant bisa
// bikin BANYAK halaman di sini, masing-masing slug sendiri (sales page per
// campaign/produk, ala Tribelio/ScaleV) — bukan menggantikan beranda.
//
// Union section SENGAJA dibuat baru (bukan memperluas landingSectionSchema
// langsung) supaya perubahan salah satu skema tidak berisiko merusak yang
// lain — meski keduanya berbagi semua 19 tipe section yang sama (di-reuse via
// `.options`) plus SATU tipe tambahan: `product_offer`.
const productOfferBase = {
  id: z.string().min(1).max(40),
  hidden: z.boolean().default(false),
};

export const productOfferSectionSchema = z.object({
  ...productOfferBase,
  type: z.literal('product_offer'),
  heading: z.string().max(120).optional(),
  /** Produk yang ditawarkan — direferensikan via slug (stabil), di-resolve saat render. */
  product: storeItemRefSchema,
  /** 'redirect' = tombol ke /order/[slug] (reuse alur checkout yang sudah ada).
   *  'embedded_form' = form order langsung di block (checkout inline, fase lanjutan). */
  mode: z.enum(['redirect', 'embedded_form']).default('redirect'),
  button_label: z.string().max(60).default('Beli Sekarang'),
});

// Block sederhana khusus builder halaman promosi (bukan beranda tenant) — sengaja
// TIDAK ditambahkan ke landingSectionSchema, mengikuti pola isolasi product_offer:
// tenant homepage sudah punya 'hero'/'about'/'gallery' yang lebih kaya struktur;
// blok di bawah ini untuk sales-page cepat ala Tribelio/Scalev (satu tujuan visual
// per blok, tanpa perlu paham HTML).
export const paragraphSectionSchema = z.object({
  ...productOfferBase,
  type: z.literal('paragraph'),
  heading: z.string().max(120).optional(),
  heading_level: z.enum(['h2', 'h3']).default('h2'),
  body: z.string().max(2000),
  align: z.enum(['left', 'center']).default('left'),
});

export const checklistSectionSchema = z.object({
  ...productOfferBase,
  type: z.literal('checklist'),
  heading: z.string().max(120).optional(),
  yes_title: z.string().max(80).default('Cocok untuk kamu yang:'),
  yes_items: z.array(z.string().min(1).max(120)).max(8).default([]),
  no_title: z.string().max(80).default('Bukan untuk kamu yang:'),
  no_items: z.array(z.string().min(1).max(120)).max(8).default([]),
});

export const campaignImageSectionSchema = z.object({
  ...productOfferBase,
  type: z.literal('image'),
  url: z.string().url().max(500),
  alt: z.string().max(160).optional(),
  caption: z.string().max(160).optional(),
  link_url: safeHref.optional(),
  max_width: z.enum(['sm', 'md', 'lg', 'full']).default('md'),
});

export const slideshowSectionSchema = z.object({
  ...productOfferBase,
  type: z.literal('slideshow'),
  slides: z
    .array(z.object({ url: z.string().url().max(500), caption: z.string().max(160).optional() }))
    .min(1)
    .max(10),
  interval_ms: z.number().int().min(2000).max(15_000).default(4000),
});

export const campaignButtonSectionSchema = z.object({
  ...productOfferBase,
  type: z.literal('button'),
  label: z.string().min(1).max(60),
  href: safeHref,
  style: z.enum(['primary', 'outline']).default('primary'),
  align: z.enum(['left', 'center']).default('center'),
});

// Kustomisasi visual per-section (khusus halaman promosi — TIDAK di landingSectionSchema
// beranda). Opt-in, disembunyikan di balik "Pengaturan Lanjutan" di editor; nilai default
// undefined di semua field berarti section memakai gaya bawaan tema seperti biasa.
export const campaignSectionStyleSchema = z.object({
  text_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  background_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  font_size: z.enum(['sm', 'base', 'lg', 'xl']).optional(),
  text_align: z.enum(['left', 'center', 'right']).optional(),
});
export type CampaignSectionStyle = z.infer<typeof campaignSectionStyleSchema>;

// Field disebut `custom_style` (BUKAN `style`) — 'button' sudah punya field
// `style: 'primary'|'outline'` sendiri (varian visual tombol); nama yang sama
// akan diam-diam menimpanya lewat `.extend()` di bawah.
const campaignSectionOptionsArray = [
  ...landingSectionSchema.options.map((s) => s.extend({ custom_style: campaignSectionStyleSchema.optional() })),
  productOfferSectionSchema.extend({ custom_style: campaignSectionStyleSchema.optional() }),
  paragraphSectionSchema.extend({ custom_style: campaignSectionStyleSchema.optional() }),
  checklistSectionSchema.extend({ custom_style: campaignSectionStyleSchema.optional() }),
  campaignImageSectionSchema.extend({ custom_style: campaignSectionStyleSchema.optional() }),
  slideshowSectionSchema.extend({ custom_style: campaignSectionStyleSchema.optional() }),
  campaignButtonSectionSchema.extend({ custom_style: campaignSectionStyleSchema.optional() }),
];
type CampaignSectionOption = (typeof campaignSectionOptionsArray)[number];
// `.map()` melebur tuple literal `landingSectionSchema.options` jadi array biasa —
// TS tak bisa lagi membuktikan "minimal 1 elemen" yang disyaratkan signature
// discriminatedUnion. Runtime-nya pasti aman (union sumber tak pernah kosong).
// Di-assert ke TUPLE dari union tipe elemen ASLINYA sendiri (`CampaignSectionOption`,
// bukan bound generik `ZodDiscriminatedUnionOption`) — kalau di-assert ke bound
// generik, TS kehilangan bentuk literal tiap anggota dan `Extract<CampaignPageSection,
// {type:'x'}>` di semua komponen renderer kolaps jadi `never`.
const campaignSectionOptions = campaignSectionOptionsArray as unknown as [
  CampaignSectionOption,
  ...CampaignSectionOption[],
];

export const campaignPageSectionSchema = z.discriminatedUnion('type', campaignSectionOptions);

export type CampaignPageSection = z.infer<typeof campaignPageSectionSchema>;

export const campaignPageStatusSchema = z.enum(['draft', 'published']);

export const createCampaignPageSchema = z.object({
  title: z.string().min(1).max(120),
  slug: z.string().min(3).max(80).regex(/^[a-z0-9-]+$/, 'Slug hanya huruf kecil, angka, dan dash'),
  status: campaignPageStatusSchema.optional(),
});

export const updateCampaignPageSchema = z.object({
  title: z.string().min(1).max(120).optional(),
  slug: z.string().min(3).max(80).regex(/^[a-z0-9-]+$/, 'Slug hanya huruf kecil, angka, dan dash').optional(),
  status: campaignPageStatusSchema.optional(),
  sections: z.array(campaignPageSectionSchema).max(20).optional(),
  seo_title: z.string().max(70).optional().nullable(),
  seo_description: z.string().max(160).optional().nullable(),
  seo_og_image: z.string().url().max(500).optional().nullable(),
});

export type CreateCampaignPageInput = z.infer<typeof createCampaignPageSchema>;
export type UpdateCampaignPageInput = z.infer<typeof updateCampaignPageSchema>;

// ── Nav overrides (app_settings key: 'nav_overrides') ──────────────────────
const navItemOverrideSchema = z
  .object({
    label: z.string().min(1).max(40).optional(),
    hidden: z.boolean().optional(),
  })
  .strict();

/** Override per-SEKSI sidebar (rename/sembunyikan grup, bukan item tunggal). */
const navSectionOverrideSchema = z
  .object({
    label: z.string().min(1).max(40).optional(),
    hidden: z.boolean().optional(),
  })
  .strict();

/**
 * Tautan menu kustom buatan admin (di LUAR item bawaan). Hanya sidebar siswa.
 * type 'internal' = path relatif di platform (safeHref); 'external' = URL http(s)
 * yang dibuka di tab baru. id stabil dibuat client (crypto.randomUUID().slice).
 */
export const navCustomLinkSchema = z
  .object({
    id: z.string().min(1).max(40),
    type: z.enum(['internal', 'external']),
    label: z.string().min(1).max(40),
    target: safeHref,
    hidden: z.boolean().default(false),
  })
  .strict();

export type NavCustomLink = z.infer<typeof navCustomLinkSchema>;

export const navOverridesSchema = z
  .object({
    user: z.record(z.string(), navItemOverrideSchema).default({}),
    admin: z.record(z.string(), navItemOverrideSchema).default({}),
    /** Key = id seksi sidebar; payload lama tanpa field ini tetap lolos (default {}). */
    user_sections: z.record(z.string(), navSectionOverrideSchema).default({}),
    admin_sections: z.record(z.string(), navSectionOverrideSchema).default({}),
    /** Tautan kustom siswa (payload lama tanpa field ini tetap lolos → default []). */
    customLinks: z.array(navCustomLinkSchema).max(10).default([]),
  })
  .strict();

export type NavOverrides = z.infer<typeof navOverridesSchema>;

/** Item inti yang tidak boleh disembunyikan tenant (server menolak hidden:true). */
export const NAV_LOCKED_IDS: Record<'user' | 'admin', readonly string[]> = {
  user: ['dashboard', 'my-classes', 'account'],
  // `settings` keluar dari daftar: halamannya dibubarkan (tarif affiliate pindah
  // ke /admin/affiliates, sisanya sudah lama di /admin/langganan dan
  // /admin/website). `website` yang sekarang memikul jaminan keterjangkauan —
  // ia satu-satunya UI yang bisa membatalkan penyembunyian menu.
  admin: ['dashboard', 'website'],
};

/** Seksi sidebar yang tidak boleh disembunyikan tenant (server menolak hidden:true). */
export const NAV_LOCKED_SECTION_IDS = {
  user: ['overview', 'account'],
  admin: ['overview', 'system'],
} as const;

// ── Konten halaman statis (app_settings key: page_*) ────────────────────────
export const CUSTOM_PAGE_KEYS = [
  'page_terms',
  'page_privacy',
  'page_refund',
  'page_faq',
  'page_contact',
  // Ditambahkan supaya tenant bisa mengganti isinya sendiri. Sebelumnya kedua
  // halaman ini TIDAK dibungkus CustomizablePage, jadi teks generik bawaan
  // platform (termasuk klaim pemakaian Google Analytics dan komisi 20/40%)
  // tampil apa adanya di semua tenant tanpa bisa diubah.
  'page_affiliate_terms',
  'page_cookies',
] as const;

export type CustomPageKey = (typeof CUSTOM_PAGE_KEYS)[number];

export const pageContentSchema = z.object({
  title: z.string().max(120).optional(),
  updated_at_label: z.string().max(40).optional(),
  /** HTML tersanitasi (sanitizeLessonHtml dijalankan server saat PUT). */
  body_html: z.string().max(50_000),
});

export type PageContent = z.infer<typeof pageContentSchema>;

// ── Nilai CSS turunan theme ──────────────────────────────────────────────────
export const THEME_RADIUS_CSS: Record<NonNullable<TenantTheme['radius']>, string> = {
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
};

export const THEME_FONT_CSS: Record<NonNullable<TenantTheme['font']>, string> = {
  system:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  inter: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  georgia: "Georgia, 'Times New Roman', serif",
  mono: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
};

/** default = --surface build statis (#F7F7F8) persis; selain itu meng-override --surface. */
export const THEME_BACKGROUND_CSS: Record<NonNullable<TenantTheme['background']>, string> = {
  default: '#F7F7F8',
  warm: '#F7F5F0',
  cool: '#F3F5F8',
  white: '#FFFFFF',
};

export interface ThemeCardCss {
  shadow: string;
  /** Warna saja (bukan shorthand) — tiap komponen section mempertahankan
   *  lebar/gaya border-nya sendiri (umumnya `border` = 1px solid Tailwind),
   *  cuma warnanya yang ikut token supaya tidak bentrok dengan varian kartu
   *  yang punya border-color kondisional sendiri (mis. pricing "highlighted"). */
  borderColor: string;
  backdrop: string;
}

/** 'flat' = tampilan sekarang (nol shadow, border transparan) — default aman,
 *  tenant existing tak berubah karena var ini hanya di-emit saat card_style
 *  eksplisit diset (lihat buildBrandCssVars). */
export const THEME_CARD_CSS: Record<NonNullable<TenantTheme['card_style']>, ThemeCardCss> = {
  flat: { shadow: 'none', borderColor: 'transparent', backdrop: 'none' },
  shadow: { shadow: '0 10px 30px rgba(0,0,0,.08)', borderColor: 'transparent', backdrop: 'none' },
  bordered: { shadow: 'none', borderColor: 'rgba(0,0,0,.14)', backdrop: 'none' },
  glass: { shadow: '0 10px 30px rgba(0,0,0,.10)', borderColor: 'rgba(255,255,255,.5)', backdrop: 'blur(16px) saturate(1.4)' },
};

export interface ThemeBackgroundStyleCss {
  image: string;
  size?: string;
}

/** 'flat' = tanpa layer (image:'none') — default aman, tenant existing tak
 *  berubah karena var ini hanya di-emit saat background_style eksplisit &
 *  bukan 'flat' (lihat buildBrandCssVars). Nilai `image` merujuk
 *  var(--brand-primary)/var(--brand-accent) lewat color-mix() — bukan hex
 *  kurasi terpisah — supaya otomatis selaras warna brand tenant apa pun. */
export const THEME_BACKGROUND_STYLE_CSS: Record<NonNullable<TenantTheme['background_style']>, ThemeBackgroundStyleCss> = {
  flat: { image: 'none' },
  gradient_soft: {
    image: 'linear-gradient(135deg, color-mix(in srgb, var(--brand-primary) 10%, transparent), color-mix(in srgb, var(--brand-accent) 14%, transparent))',
  },
  gradient_radial: {
    image: 'radial-gradient(circle at 15% 10%, color-mix(in srgb, var(--brand-accent) 16%, transparent), transparent 60%), radial-gradient(circle at 85% 90%, color-mix(in srgb, var(--brand-primary) 14%, transparent), transparent 55%)',
  },
  pattern_dots: {
    image: 'radial-gradient(color-mix(in srgb, var(--brand-primary) 24%, transparent) 1.2px, transparent 1.6px)',
    size: '22px 22px',
  },
  pattern_grid: {
    image: 'linear-gradient(color-mix(in srgb, var(--brand-primary) 8%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--brand-primary) 8%, transparent) 1px, transparent 1px)',
    size: '28px 28px',
  },
};

// ── Konfigurasi web lain (app_settings, whitelist WEBSITE_CONFIG_SCHEMAS) ────

/** Penandatangan sertifikat per tenant (app_settings key: 'certificate_config'). */
export const certificateConfigSchema = z
  .object({
    signatory_name: z.string().min(1).max(80),
    signatory_title: z.string().max(80).optional(),
    signature_image_url: z.string().url().max(500).optional(),
    /** Warna aksen bingkai/garis sertifikat — absen = warna brand primary. */
    accent_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    /** Label teks di sertifikat — absen = copy default Indonesia. */
    labels: z
      .object({
        title: z.string().max(40).optional(),
        subtitle: z.string().max(60).optional(),
        given_to: z.string().max(40).optional(),
        for_completing: z.string().max(60).optional(),
      })
      .strict()
      .optional(),
    /** @deprecated dipertahankan untuk kompatibilitas mundur (schema .strict()
     *  menolak key tak dikenal — tenant lama yang sudah simpan `font` akan gagal
     *  parse kalau field ini dihapus). UI baru menulis `template`, bukan ini. */
    font: z.enum(['classic', 'modern']).optional(),
    /** Template layout sertifikat — absen = turunan dari `font` lama (lihat
     *  pemanggil). Semua tetap landscape A4; beda di border/susunan/penempatan. */
    template: z.enum(['classic', 'modern-minimal', 'formal-frame', 'color-block']).optional(),
  })
  .strict();

export type CertificateConfig = z.infer<typeof certificateConfigSchema>;

/** Identitas usaha untuk invoice/kuitansi (app_settings key: 'business_profile'). */
export const businessProfileSchema = z
  .object({
    company_name: z.string().max(120).optional(),
    npwp: z.string().max(30).regex(/^[0-9.\-\s]*$/, 'NPWP hanya angka, titik, strip').optional(),
    address: z.string().max(400).optional(),
    phone: z.string().max(25).regex(/^[+0-9\s\-()]*$/).optional(),
    /** Multiline "Bank BCA\na.n. ..." — prefill Informasi Pembayaran form invoice. */
    payment_info: z.string().max(500).optional(),
    invoice_notes: z.string().max(300).optional(),
  })
  .strict();

export type BusinessProfile = z.infer<typeof businessProfileSchema>;

/** Copy halaman login/register (app_settings key: 'auth_config'). */
export const authConfigSchema = z
  .object({
    login_headline: z.string().max(80).optional(),
    login_subtitle: z.string().max(160).optional(),
    register_headline: z.string().max(80).optional(),
    register_subtitle: z.string().max(160).optional(),
    /**
     * Kebijakan pendaftaran siswa:
     *   'open'        — siapa pun bisa daftar (default).
     *   'invite_only' — hanya lewat undangan/link admin (register publik ditolak).
     *   'closed'      — pendaftaran ditutup total.
     */
    registration_mode: z.enum(['open', 'invite_only', 'closed']).optional(),
    /** Opsi dropdown "Tau dari mana?" — kosong = daftar generik bawaan. */
    referral_sources: z.array(z.string().min(1).max(60)).max(12).optional(),
    /** Panel kiri halaman auth (desktop) — absen = ilustrasi default. */
    panel: z
      .object({
        style: z.enum(['illustration', 'image', 'gradient', 'none']).default('illustration'),
        /** Hanya dipakai style 'image'. */
        image_url: z.string().url().max(500).optional(),
        overlay_heading: z.string().max(80).optional(),
        overlay_points: z.array(z.string().min(1).max(80)).max(4).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export type AuthConfig = z.infer<typeof authConfigSchema>;

/** Kuesioner onboarding member baru (app_settings key: 'onboarding_config'). */
export const onboardingConfigSchema = z
  .object({
    // Default OFF — tenant yang belum pernah menyentuh menu Website > Onboarding
    // TIDAK boleh memaksa kuis ini ke member baru. Hanya aktif kalau admin
    // eksplisit menyalakannya.
    enabled: z.boolean().default(false),
    /** Absen = 3 langkah default platform; tenant boleh override sebagian. */
    steps: z
      .array(
        z.object({
          key: z.enum(['role', 'primary_goal', 'ai_level']),
          question: z.string().min(1).max(120),
          options: z
            .array(
              z.object({
                /** Nilai tersimpan di profil user — snake_case stabil. */
                value: z.string().min(1).max(40).regex(/^[a-z0-9_]+$/),
                label: z.string().min(1).max(60),
              })
            )
            .min(2)
            .max(8),
        })
      )
      .max(3)
      .optional(),
  })
  .strict();

export type OnboardingConfig = z.infer<typeof onboardingConfigSchema>;

/** Fitur platform yang bisa dimatikan per tenant (app_settings key: 'feature_overrides'). */
export const FEATURE_TOGGLE_KEYS = [
  'affiliate',
  'gamification',
  'webinar',
  'community',
  'tryout',
] as const;

export type FeatureToggleKey = (typeof FEATURE_TOGGLE_KEYS)[number];

/** Absen/undefined per key = default fitur (mengikuti tier tenant). */
export const featureOverridesSchema = z
  .object({
    affiliate: z.boolean().optional(),
    gamification: z.boolean().optional(),
    webinar: z.boolean().optional(),
    community: z.boolean().optional(),
    tryout: z.boolean().optional(),
  })
  .strict();

export type FeatureOverrides = z.infer<typeof featureOverridesSchema>;

/** Elemen dashboard member yang bisa disembunyikan (app_settings key: 'dashboard_config'). */
export const dashboardConfigSchema = z
  .object({
    show_premium_cta: z.boolean().optional(),
    show_recommendations: z.boolean().optional(),
  })
  .strict();

export type DashboardConfig = z.infer<typeof dashboardConfigSchema>;

/** Mode monetisasi tenant (app_settings key: 'monetization_mode'); absen → 'membership'. */
export const monetizationModeSchema = z.enum(['membership', 'per_class', 'mixed', 'digital_product']);

export type MonetizationMode = z.infer<typeof monetizationModeSchema>;

/** Istilah UI yang bisa diganti tenant (app_settings key: 'terminology'). */
export const TERMINOLOGY_KEYS = [
  'course',
  'member',
  'tryout',
  'premium',
  'certificate',
  'bonus',
] as const;

export type TerminologyKey = (typeof TERMINOLOGY_KEYS)[number];

/** Absen per key = istilah default platform. */
export const terminologySchema = z
  .object({
    course: z.string().min(1).max(24).optional(),
    member: z.string().min(1).max(24).optional(),
    tryout: z.string().min(1).max(24).optional(),
    premium: z.string().min(1).max(24).optional(),
    certificate: z.string().min(1).max(24).optional(),
    bonus: z.string().min(1).max(24).optional(),
  })
  .strict();

export type Terminology = z.infer<typeof terminologySchema>;

/** Halaman dukungan/tip (app_settings key: 'tip_config') — order_type 'tip'. */
export const tipConfigSchema = z
  .object({
    enabled: z.boolean().default(false),
    title: z.string().max(80).default('Dukung Kami'),
    message: z.string().max(500).optional(),
    /** Pilihan nominal cepat (rupiah, integer >= 5000) — maks 6 tombol. */
    preset_amounts: z
      .array(z.number().int().min(5000))
      .max(6)
      .default([10000, 25000, 50000]),
    /** Target penggalangan (progress bar) — absen/null = tanpa target.
     *  `.nullish()` — UI (DukunganTab) mengirim `null` saat field target kosong
     *  (default), bukan `undefined`; tanpa nullable zod menolak → 400 saat simpan. */
    target_amount: z.number().int().positive().nullish(),
    /** Pesan terima kasih setelah tip berhasil. */
    thank_you: z.string().max(300).optional(),
  })
  .strict();

export type TipConfig = z.infer<typeof tipConfigSchema>;

/**
 * Kontak bantuan tenant (app_settings key: 'support_contact'). Nomor WA admin
 * mengaktifkan widget chat bantuan mengambang di dashboard siswa; help_links =
 * tautan bantuan eksternal (mis. pusat bantuan, grup Telegram).
 */
export const supportContactSchema = z
  .object({
    /** Nomor WA admin (format Indonesia). Absen/kosong → widget tidak tampil. */
    wa_admin_number: z
      .string()
      .regex(/^(\+62|62|0)8\d{7,11}$/, 'Nomor WA harus format Indonesia (mis. 08123456789)')
      .optional(),
    /** Pesan awal yang terisi otomatis saat siswa klik widget WA. */
    wa_greeting: z.string().max(200).optional(),
    /** Tautan bantuan eksternal (label + URL) — maks 8. */
    help_links: z
      .array(z.object({ label: z.string().min(1).max(40), url: safeHref }))
      .max(8)
      .default([]),
  })
  .strict();

export type SupportContact = z.infer<typeof supportContactSchema>;

/**
 * Pengumuman di atas dashboard siswa (app_settings key: 'announcement_config').
 * Banner sengaja tanpa gaya/warna pilihan — selalu ikut var(--brand-primary)
 * tenant, konsisten dengan elemen brand lain.
 */
export const announcementConfigSchema = z
  .object({
    enabled: z.boolean().default(false),
    message: z.string().max(200).default(''),
    /** Tautan opsional di banner — kosong = banner teks polos tanpa tombol. */
    link_url: safeHref.optional(),
    link_label: z.string().max(30).optional(),
  })
  .strict();

export type AnnouncementConfig = z.infer<typeof announcementConfigSchema>;

/**
 * Notifikasi email admin (app_settings key: 'admin_notifications_config').
 * Default OFF — tenant yang belum pernah menyimpan pengaturan ini TIDAK
 * boleh diam-diam mengirim email ke siapa pun (konsisten dengan pola
 * onboarding_config: aktif hanya kalau admin eksplisit menyalakannya).
 */
export const adminNotificationsConfigSchema = z
  .object({
    enabled: z.boolean().default(false),
    /** Absen = fallback ke branding.support_email saat dikirim. */
    recipient_email: z.string().email().max(200).optional(),
    events: z
      .object({
        new_user_registered: z.boolean().default(true),
        new_order_created: z.boolean().default(false),
        order_paid: z.boolean().default(true),
        webinar_free_registration: z.boolean().default(true),
        private_consultation_order: z.boolean().default(true),
      })
      .strict()
      .default({}),
  })
  .strict();

export type AdminNotificationsConfig = z.infer<typeof adminNotificationsConfigSchema>;

/**
 * Status checklist setup admin (app_settings key: 'admin_setup_progress').
 *
 * CATATAN: app_settings itu per-TENANT, bukan per-user. Semua field di sini
 * berlaku untuk seluruh admin tenant sekaligus — termasuk snooze. Status
 * per-admin (rute yang sudah dilihat, tur yang sudah dijalankan) TIDAK di sini;
 * itu ada di tabel user_milestones. Lihat packages/shared/src/admin-guide.ts.
 *
 * Skema ini .strict() DAN divalidasi saat tulis di PUT /api/admin/website/config/:key,
 * jadi field yang tidak terdaftar membuat seluruh permintaan gagal 400.
 */
export const adminSetupProgressSchema = z
  .object({
    dismissed: z.boolean().default(false),
    /** ISO timestamp saat checklist ditutup permanen. */
    dismissed_at: z.string().optional(),
    /** Wizard brand quick-start (first-run) sudah diselesaikan/di-skip. */
    wizard_done: z.boolean().optional(),
    wizard_done_at: z.string().optional(),
    /**
     * Kartu Misi disembunyikan sampai tanggal ini (ISO). Ditulis tombol tunda.
     * Sebelumnya dua field ini dipakai UI tapi tidak ada di skema, sehingga
     * setiap penundaan dibalas 400 dan tombolnya tidak pernah benar-benar bekerja.
     */
    snoozed_until: z.string().optional(),
    snoozed_at: z.string().optional(),
    /** Kapan semua misi yang berlaku pertama kali selesai — animasi selesai
     *  hanya diputar sekali seumur tenant. */
    mission_completed_at: z.string().optional(),
  })
  .strict();

export type AdminSetupProgress = z.infer<typeof adminSetupProgressSchema>;

/**
 * Registry key app_settings yang boleh dibaca/ditulis admin tenant via
 * /api/admin/website/config/:key — SATU sumber whitelist; key di luar ini
 * (mailketing_api_token dkk) tetap super_admin-only lewat route settings generik.
 */
export const WEBSITE_CONFIG_SCHEMAS = {
  certificate_config: certificateConfigSchema,
  business_profile: businessProfileSchema,
  auth_config: authConfigSchema,
  email_footer_text: z.string().max(300),
  /** Gaya header email transaksional — 'logo' (bawaan, logo+nama), 'bar' (bar
   *  aksen penuh-lebar+nama besar, tanpa logo), 'minimal' (nama kecil saja). */
  email_header_style: z.enum(['logo', 'bar', 'minimal']).default('logo'),
  onboarding_config: onboardingConfigSchema,
  feature_overrides: featureOverridesSchema,
  dashboard_config: dashboardConfigSchema,
  monetization_mode: monetizationModeSchema,
  terminology: terminologySchema,
  admin_setup_progress: adminSetupProgressSchema,
  tip_config: tipConfigSchema,
  support_contact: supportContactSchema,
  store_config: storeConfigSchema,
  // Kode undangan pendaftaran (dipakai saat registration_mode='invite_only').
  // Admin-only via /config; TIDAK diekspos ke tenant-config publik.
  registration_invite_code: z.string().max(120),
  announcement_config: announcementConfigSchema,
  admin_notifications_config: adminNotificationsConfigSchema,
} as const;

export type WebsiteConfigKey = keyof typeof WEBSITE_CONFIG_SCHEMAS;

// ── Token personalisasi email sequence ───────────────────────────────────────

/**
 * Token kanonis snake_case yang boleh dipakai di subject/body sequence email
 * (format {{token}}). Engine render juga tetap menerima alias camelCase lama
 * (mis. {{firstName}}) demi back-compat — daftar ini untuk UI picker & validasi.
 */
export const SEQUENCE_TOKENS = [
  'first_name',
  'user_name',
  'course_name',
  'product_name',
  'checkout_url',
  'dashboard_url',
  'unsubscribe_url',
  'brand_name',
  'app_url',
  'support_url',
  'community_whatsapp_url',
] as const;

export type SequenceToken = (typeof SEQUENCE_TOKENS)[number];

/**
 * Event lifecycle siswa yang boleh dipilih admin saat membuat sequence email
 * custom. HANYA event yang benar-benar dipancarkan platform (startSequence
 * dipanggil di titik-titik ini) — sequence custom dengan trigger di daftar ini
 * ikut jalan otomatis lewat engine, tanpa broadcast. Trigger di luar daftar ini
 * tak akan pernah menyala, jadi tidak diizinkan.
 */
export const SEQUENCE_CUSTOM_TRIGGERS = [
  { event: 'user_registered', label: 'Siswa baru mendaftar' },
  { event: 'payment_successful', label: 'Pembayaran berhasil' },
  { event: 'checkout_started', label: 'Checkout ditinggalkan' },
  { event: 'webinar_registered', label: 'Mendaftar webinar' },
  { event: 'course_inactive', label: 'Berhenti di tengah kelas' },
  { event: 'user_inactive_30d', label: 'Tidak aktif 30 hari' },
  { event: 'membership_expiring', label: 'Membership akan berakhir' },
] as const;

export type SequenceCustomTrigger = (typeof SEQUENCE_CUSTOM_TRIGGERS)[number]['event'];

export const SEQUENCE_CUSTOM_TRIGGER_EVENTS: readonly string[] =
  SEQUENCE_CUSTOM_TRIGGERS.map((t) => t.event);
